import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';
import type { SanityImage } from '../types/sanity';

const envProjectId = import.meta.env.VITE_SANITY_PROJECT_ID;
const envDataset = import.meta.env.VITE_SANITY_DATASET;

// Fallbacki, aby nie wywalać produkcji w razie braku zmiennych środowiskowych
const projectId = (envProjectId as string | undefined);
const dataset = (envDataset as string | undefined);

if (!envProjectId || !envDataset) {
  console.warn('Sanity: użyto wartości domyślnych. Skonfiguruj VITE_SANITY_PROJECT_ID i VITE_SANITY_DATASET w środowisku.');
}

// Uwaga: token nie jest podawany do klienta po stronie przeglądarki,
// aby nie wyciekał do bundla. Dataset musi być publiczny.
export const client = createClient({
  projectId,
  dataset,
  useCdn: true,
  apiVersion: '2024-03-20'
});

const builder = imageUrlBuilder(client);

export function urlFor(source: SanityImage | null) {
  if (!source || !source.asset || !source.asset._ref) {
    console.warn('Invalid image source:', source);
    return 'https://placehold.co/800x600/jpeg';
  }

  try {
    return builder.image(source);
  } catch (error) {
    console.error('Error building image URL:', error);
    return 'https://placehold.co/800x600/jpeg';
  }
}

/**
 * Generate optimized image URL with WebP format and quality settings
 */
export function urlForOptimized(
  source: SanityImage | null,
  width?: number,
  height?: number,
  quality: number = 80
) {
  if (!source || !source.asset || !source.asset._ref) {
    return 'https://placehold.co/800x600/jpeg';
  }

  try {
    let urlBuilder = builder.image(source).format('webp').quality(quality);
    
    if (width) {
      urlBuilder = urlBuilder.width(width);
    }
    if (height) {
      urlBuilder = urlBuilder.height(height);
    }
    
    return urlBuilder.url();
  } catch (error) {
    console.error('Error building optimized image URL:', error);
    return 'https://placehold.co/800x600/jpeg';
  }
}

/**
 * Generate responsive srcset for different viewport sizes
 */
export function generateSrcSet(source: SanityImage | null, widths: number[] = [400, 600, 800, 1000, 1200, 1600]): string {
  if (!source || !source.asset || !source.asset._ref) {
    return '';
  }

  return widths
    .map(width => {
      try {
        const url = urlForOptimized(source, width, undefined, 80);
        return `${url} ${width}w`;
      } catch {
        return '';
      }
    })
    .filter(Boolean)
    .join(', ');
}

// Automatyczne generowanie alt textu i caption na podstawie danych zwierzaka
export function generatePetImageAlt(pet: { name: string; gender?: 'male' | 'female' | 'unknown'; mainCategory: 'dog' | 'cat' }): string {
  const petName = pet.name || '';
  const petGender = pet.gender || 'unknown';
  const petCategory = pet.mainCategory || 'dog';
  
  const genderText = petGender === 'male' ? 'samiec' : petGender === 'female' ? 'samica' : '';
  const categoryText = petCategory === 'dog' ? 'pies' : 'kot';
  
  if (genderText && petName) {
    return `${petName} - ${genderText} ${categoryText}`;
  } else if (petName) {
    return `${petName} - ${categoryText}`;
  } else {
    return categoryText;
  }
}

export function getImageAlt(image: SanityImage | undefined, fallbackPet?: { name: string; gender?: 'male' | 'female' | 'unknown'; mainCategory: 'dog' | 'cat' }): string {
  if (image?.alt) {
    return image.alt;
  }
  if (fallbackPet) {
    return generatePetImageAlt(fallbackPet);
  }
  return 'Zdjęcie';
}

export function getImageCaption(image: SanityImage | undefined, fallbackPet?: { name: string; gender?: 'male' | 'female' | 'unknown'; mainCategory: 'dog' | 'cat' }): string {
  if (image?.caption) {
    return image.caption;
  }
  if (fallbackPet) {
    return generatePetImageAlt(fallbackPet);
  }
  return '';
}